import { create } from 'zustand';
import questionnaireData from '../data/questionnaires.json';
import { withoutRetiredQuestions } from '../utils/retiredQuestions'; // s126: turned-off question types (Epworth)

const API_BASE = import.meta.env.VITE_API_URL || 
  (import.meta.env.PROD ? 'https://api.orellahealth.com/api/v1' : 'http://localhost:8000/api/v1');

// '3.6' vs '3.5' → true. Missing or unparsable versions are never "newer" (the live catalog wins).
export function isNewerVersion(a, b) {
  const pa = String(a || '').split('.').map(Number)
  const pb = String(b || '').split('.').map(Number)
  if (!pa.length || pa.some(Number.isNaN) || pb.some(Number.isNaN) || !b) return false
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] || 0
    const y = pb[i] || 0
    if (x !== y) return x > y
  }
  return false
}

const useQuestionnaireStore = create((set, get) => ({
  // Data - starts with local JSON, can be updated from API
  specialties: withoutRetiredQuestions(questionnaireData.specialties),
  loading: false,
  lastFetched: null,
  lastFetchedLang: null,
  error: null,
  source: 'local', // 'local' | 'api'
  
  // v129.0.0: Fetch questionnaires from API with optional language parameter.
  // v234.10.0: DB is the SOURCE OF TRUTH. The admin panel publishes the live catalog
  // and the app reads it; the bundled questionnaires.json is now an OFFLINE FALLBACK
  // only (used when the API is empty or unreachable). This replaces the v178.0.0
  // all-or-nothing version gate, which silently reverted to bundled data on any version
  // drift and dropped admin edits. Translation safety is preserved per-question: tq()/to()
  // fall back to English for any text not yet translated, and when lang!=en the API already
  // overlays translatedText/translatedOptions — so accepting the live catalog can never
  // break a survey. The DB stays current because the admin seeds/reconciles it from the
  // shipped catalog on each release.
  fetchQuestionnaires: async (lang) => {
    set({ loading: true, error: null });
    try {
      const langParam = lang && lang !== 'en' ? `?lang=${lang}` : '';
      const response = await fetch(`${API_BASE}/questionnaires${langParam}`);
      if (response.ok) {
        const data = await response.json();
        // s100 (2026-10-11): ONE exception to "the DB wins" — a bundled catalog whose version is STRICTLY
        // NEWER than the live one. That is the window between an app release that ships a new catalog
        // (and the engine for it) and the admin seeding the DB from it; in that window the live list is
        // the older one and must not hide the new areas. Admin edits do not move the version, so at an
        // equal version the live catalog (with its edits) still wins, as v234.10.0 intended.
        if (data.specialties && data.specialties.length > 0 && isNewerVersion(questionnaireData.version, data.version)) {
          set({ specialties: withoutRetiredQuestions(questionnaireData.specialties), loading: false, source: 'local' });
          console.log(`[Questionnaires] Bundled catalog v${questionnaireData.version} is newer than live v${data.version} — using bundled until the DB is seeded`);
          return false;
        }
        if (data.specialties && data.specialties.length > 0) {
          set({
            specialties: withoutRetiredQuestions(data.specialties),
            lastFetched: new Date().toISOString(),
            lastFetchedLang: lang || 'en',
            loading: false,
            source: data.source || 'api'
          });
          console.log(`[Questionnaires] Live catalog from API accepted (v${data.version || '?'}, lang=${lang || 'en'})`);
          return true;
        }
      }
      // API empty/unreachable — keep the bundled fallback catalog so surveys always work.
      set({ loading: false, source: 'local' });
      console.log('[Questionnaires] API empty — using bundled fallback catalog');
      return false;
    } catch (err) {
      console.error('[Questionnaires] Fetch error:', err);
      set({ loading: false, error: err.message, source: 'local' });
      // Keep the bundled fallback on error — surveys must always work.
      return false;
    }
  },
  
  // Getters - with flexible matching by ID or name
  getSpecialty: (specialtyId) => {
    if (!specialtyId) return null;
    const specialties = get().specialties;
    // Try exact ID match first
    let specialty = specialties.find(s => s.id === specialtyId);
    if (specialty) return specialty;
    
    // v230.2.1 — Normalize: lowercase, collapse "&"/spaces AND underscores to
    // hyphens. Specialty IDs are hyphenated (stomach-gut, head-neck), but some
    // callers (chat agent deep-links) passed underscores (stomach_gut) → no
    // match → FocusAreas "Something went wrong" fallback. Underscore→hyphen here
    // makes the resolver robust to either form.
    const normalizedId = specialtyId.toLowerCase()
      .replace(/\s+&\s+/g, '-').replace(/[\s_]+/g, '-');
    specialty = specialties.find(s => s.id === normalizedId);
    if (specialty) return specialty;
    
    // Try name match
    specialty = specialties.find(s => 
      s.name?.toLowerCase() === specialtyId.toLowerCase() ||
      s.name?.toLowerCase().replace(/\s+&\s+/g, '-').replace(/[\s_]+/g, '-') === normalizedId
    );
    return specialty || null;
  },
  
  getFocusArea: (specialtyId, focusAreaId) => {
    const specialty = get().getSpecialty(specialtyId);
    if (!specialty || !focusAreaId) return null;
    
    // Try exact ID match first
    let focusArea = specialty.focusAreas.find(fa => fa.id === focusAreaId);
    if (focusArea) return focusArea;
    
    // Try normalized ID match
    const normalizedId = focusAreaId.toLowerCase().replace(/\s+/g, '-');
    focusArea = specialty.focusAreas.find(fa => fa.id === normalizedId);
    if (focusArea) return focusArea;
    
    // Try name match
    focusArea = specialty.focusAreas.find(fa => 
      fa.name?.toLowerCase() === focusAreaId.toLowerCase() ||
      fa.name?.toLowerCase().replace(/\s+/g, '-') === normalizedId
    );
    if (focusArea) return focusArea;

    // v207.1.0: Tolerant name-match fallback for resume-from-draft.
    // The API stores the human-readable focus area name (e.g., "Globus (Lump in Throat)")
    // and `loadCases` algorithmically derives an ID by stripping parens + spacing → dashes
    // ("globus-lump-in-throat"). But the catalog's actual ID may be shorter ("globus-lump").
    // To recover, normalize BOTH the catalog name and the incoming ID/name to a common
    // alphanumeric-only token list and check for substring containment in either direction.
    // This catches cases where the API name expands or contracts vs the catalog ID.
    const tokenize = (s) => (s || '').toLowerCase().replace(/[()]/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean);
    const incomingTokens = tokenize(focusAreaId);
    if (incomingTokens.length > 0) {
      // Score each focus area by token overlap; pick the best match if it's strong enough.
      let best = null;
      let bestScore = 0;
      for (const fa of specialty.focusAreas) {
        const idTokens = tokenize(fa.id);
        const nameTokens = tokenize(fa.name);
        // Score = (matched id tokens × 2) + (matched name tokens × 1)
        const idMatches = idTokens.filter(t => incomingTokens.includes(t)).length;
        const nameMatches = nameTokens.filter(t => incomingTokens.includes(t)).length;
        const score = idMatches * 2 + nameMatches;
        // Require ALL of the catalog's id tokens to appear in the incoming tokens — this
        // is what makes "globus-lump-in-throat" match "globus-lump" but NOT "globus".
        const allIdTokensPresent = idTokens.length > 0 && idTokens.every(t => incomingTokens.includes(t));
        if (allIdTokensPresent && score > bestScore) {
          best = fa;
          bestScore = score;
        }
      }
      if (best) return best;
    }

    return null;
  },
  
  getQuestions: (specialtyId, focusAreaId, category) => {
    const focusArea = get().getFocusArea(specialtyId, focusAreaId);
    if (!focusArea) return [];
    return focusArea.questions[category] || [];
  },
  
  // Get all focus areas for a specialty
  getFocusAreas: (specialtyId) => {
    const specialty = get().getSpecialty(specialtyId);
    if (!specialty) return [];
    return specialty.focusAreas;
  },
}));

export default useQuestionnaireStore;
